/**
 * Which page the apex root ("/") renders — resolved once per app session from the
 * public config endpoint and mirrored to localStorage so repeat visits paint the
 * right page instantly (no spinner). The super-admin sets it in Plans & billing.
 *
 * Three choices: the classic landing, the built-in sales page, or one of the
 * super-admin's own landing-library pages ("page"), whose full payload comes back
 * inline with the choice so the root still paints from ONE request.
 */
import type { HomepageChoice, PublicPage } from "@shared/types";
import { resources } from "./resources";

export interface HomepageResolution {
  homepage: HomepageChoice;
  /** Only set when homepage === "page". */
  page: PublicPage | null;
}

// v2: the stored value used to be a bare "landing"/"sales" string under a different
// key. Bumping it stops a returning visitor's cached choice from being read under
// the new three-way semantics before the server has confirmed it.
const STORAGE_KEY = "de:homepage:v2";

// undefined = not resolved this session yet.
let cached: HomepageResolution | undefined;
let inflight: Promise<HomepageResolution> | null = null;

/** Only the CHOICE is persisted — a whole page payload is too big for (and goes
 *  stale in) localStorage, so a "page" root re-fetches before its first paint. */
function readStored(): HomepageChoice | undefined {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "sales" || v === "landing" || v === "page" ? v : undefined;
  } catch {
    return undefined;
  }
}

/** Last-known resolution for a zero-flash first paint (may be stale; refreshed in
 *  bg). A "page" choice has no cached payload, so it reports "not resolved" and
 *  the caller keeps the spinner up for the one fetch. */
export function cachedHomepage(): HomepageResolution | undefined {
  if (cached) return cached;
  const stored = readStored();
  if (stored === "landing" || stored === "sales") return { homepage: stored, page: null };
  return undefined;
}

/** Resolve (and cache) the homepage choice from the public config endpoint. */
export function ensureHomepage(): Promise<HomepageResolution> {
  if (cached !== undefined) return Promise.resolve(cached);
  if (!inflight) {
    inflight = resources
      .homepageChoice()
      .then((r): HomepageResolution => {
        if (r.homepage === "page" && r.page) return { homepage: "page", page: r.page };
        return { homepage: r.homepage === "sales" ? "sales" : "landing", page: null };
      })
      .catch((): HomepageResolution => {
        const stored = readStored();
        return { homepage: stored === "sales" ? "sales" : "landing", page: null };
      })
      .then((h) => {
        cached = h;
        try {
          localStorage.setItem(STORAGE_KEY, h.homepage);
        } catch {
          /* storage may be unavailable; the in-memory cache still holds */
        }
        return h;
      });
  }
  return inflight;
}

/** Drop the cached choice so the next resolve re-reads the server (after a change). */
export function clearHomepageCache(): void {
  cached = undefined;
  inflight = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
